import { formatDateLocal } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import { DataTable, type TanstackReactTable } from "@devhop/ui";
import { EyeIcon, EyeOffIcon, TrashIcon } from "lucide-react";
import type { ReviewDTO } from "../api/client";
import { StarRating } from "./StarRating";

type ReviewsTableProps = {
  canManage: boolean;
  isLoading: boolean;
  data: ReviewDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onView: (row: ReviewDTO) => void;
  onToggleHidden: (row: ReviewDTO) => void;
  onDelete: (id: string) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
};

export function ReviewsTable({
  canManage,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onView,
  onToggleHidden,
  onDelete,
  onPaginationChange,
  onSortingChange,
}: ReviewsTableProps) {
  const columns: TanstackReactTable.ColumnDef<ReviewDTO>[] = [
    {
      id: "productName",
      header: "ສິນຄ້າ",
      cell: ({ row }) => (
        <span className="font-medium">
          {row.original.productName ?? row.original.productSlug ?? "—"}
        </span>
      ),
    },
    {
      id: "authorDisplayName",
      header: "ຜູ້ເຂົ້າຮ່ວມ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.authorDisplayName ?? row.original.authorName ?? "—"}
        </span>
      ),
    },
    {
      id: "rating",
      header: "ຄະແນນ",
      cell: ({ row }) => <StarRating rating={row.original.rating} />,
    },
    {
      id: "content",
      header: "ເນື້ອໃນ",
      cell: ({ row }) => (
        <span
          className="line-clamp-2 max-w-xs text-muted-foreground text-sm"
          title={row.original.content ?? ""}
        >
          {row.original.content
            ? row.original.content.length > 80
              ? row.original.content.slice(0, 80) + "…"
              : row.original.content
            : "—"}
        </span>
      ),
    },
    {
      id: "hidden",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <span
          className={
            row.original.hidden
              ? "text-muted-foreground text-sm"
              : "text-foreground text-sm"
          }
        >
          {row.original.hidden ? "ຊ້ອນ" : "ສະແດງ"}
        </span>
      ),
    },
    {
      id: "createdAt",
      header: "ວັນທີ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateLocal(row.original.createdAt)}
        </span>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => {
        if (!canManage) return null;
        const actions = [
          {
            label: "ເບິ່ງ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onView(row.original),
          },
          {
            label: row.original.hidden ? "ເປີດໃຫ້ສະແດງ" : "ຊ້ອນ",
            icon: row.original.hidden ? (
              <EyeIcon className="h-4 w-4" />
            ) : (
              <EyeOffIcon className="h-4 w-4" />
            ),
            onClick: () => onToggleHidden(row.original),
          },
          {
            label: "ລຶບ",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: () => onDelete(row.original.id),
            variant: "destructive" as const,
          },
        ];
        return <RowActions actions={actions} maxInline={3} />;
      },
    },
  ];

  return (
    <DataTable<ReviewDTO, unknown>
      noDataMessage="ບໍ່ພົບຄຳເຫັນ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(s) => {
        if (s[0]?.id === "") return;
        onSortingChange(s[0]?.id || "", s[0]?.desc || false);
      }}
    />
  );
}
